import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { BoatState, GameState, Buoy, StartFinishLine, Vector2D } from './types';
import {
  INITIAL_BOAT_STATE,
  INITIAL_WIND_STATE,
  INITIAL_GAME_STATE,
} from './data/boatConfig';
import {
  DEFAULT_BUOYS_PERCENT,
  START_FINISH_LINE_PERCENT,
  scaleBuoys,
  scaleStartFinishLine,
  ROUNDING_ZONE_RADIUS,
} from './data/raceCourse';
import { useGameLoop } from './hooks/useGameLoop';
import { useKeyboardInput } from './hooks/useKeyboardInput';
import { useWind } from './hooks/useWind';
import { updateBoatPhysics } from './engine/physics';
import { distance, subtractVectors, normalizeAngle, lerp } from './engine/geometry';
import { renderFrame, Camera } from './rendering/renderer';
import { CoastLine, generateCoastLine, constrainToWater } from './rendering/coastRenderer';
import { HUD } from './components/HUD';
import { ModeSelector } from './components/ModeSelector';
import { RaceTimer } from './components/RaceTimer';
import { Instructions } from './components/Instructions';

const BOAT_SCALE = 0.6;
const CAMERA_SMOOTHING = 0.08; // How smoothly the camera follows (0-1, lower = smoother)

// Check if boat crossed a line (start/finish)
function crossedLine(
  prevPos: Vector2D,
  newPos: Vector2D,
  lineStart: Vector2D,
  lineEnd: Vector2D
): boolean {
  const d1 = (newPos.x - lineStart.x) * (lineEnd.y - lineStart.y) -
             (newPos.y - lineStart.y) * (lineEnd.x - lineStart.x);
  const d2 = (prevPos.x - lineStart.x) * (lineEnd.y - lineStart.y) -
             (prevPos.y - lineStart.y) * (lineEnd.x - lineStart.x);

  if (d1 * d2 >= 0) return false;

  const d3 = (lineStart.x - prevPos.x) * (newPos.y - prevPos.y) -
             (lineStart.y - prevPos.y) * (newPos.x - prevPos.x);
  const d4 = (lineEnd.x - prevPos.x) * (newPos.y - prevPos.y) -
             (lineEnd.y - prevPos.y) * (newPos.x - prevPos.x);

  return d3 * d4 < 0;
}

// Check if boat rounded a buoy correctly
function checkBuoyRounded(
  prevPos: Vector2D,
  newPos: Vector2D,
  buoy: Buoy,
  boatHeading: number
): boolean {
  const dist = distance(newPos, buoy.position);

  if (dist > ROUNDING_ZONE_RADIUS) return false;

  const toBuoy = subtractVectors(buoy.position, newPos);
  const angleTobuoy = Math.atan2(toBuoy.x, -toBuoy.y);
  const relativeAngle = normalizeAngle(angleTobuoy - boatHeading);

  if (buoy.roundingDirection === 'port') {
    return relativeAngle < 0;
  } else {
    return relativeAngle > 0;
  }
}

const Sailing: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { isDarkMode } = useTheme();

  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [worldSize, setWorldSize] = useState({ width: 800, height: 600 });
  const [boat, setBoat] = useState<BoatState>(() => ({
    ...INITIAL_BOAT_STATE,
    position: { x: 280, y: 420 },
  }));
  const [camera, setCamera] = useState<Camera>({ x: 0, y: 0 });
  const prevBoatPosRef = useRef<Vector2D>(boat.position);
  const { wind, updateWind } = useWind(INITIAL_WIND_STATE);
  const [gameState, setGameState] = useState<GameState>(INITIAL_GAME_STATE);
  const [buoys, setBuoys] = useState<Buoy[]>([]);
  const [startFinishLine, setStartFinishLine] = useState<StartFinishLine | null>(null);
  const [coast, setCoast] = useState<CoastLine | null>(null);
  const [showInstructions, setShowInstructions] = useState(true);
  const [rudderInput, setRudderInput] = useState(0);
  const [buoyProximity, setBuoyProximity] = useState<boolean[]>([]);

  useEffect(() => {
    const updateSize = () => {
      const container = canvasRef.current?.parentElement;
      if (container) {
        const width = container.clientWidth;
        const height = container.clientHeight;
        setCanvasSize({ width, height });
        setWorldSize({ width, height });

        const scaledBuoys = scaleBuoys(DEFAULT_BUOYS_PERCENT, width, height);
        setBuoys(scaledBuoys);
        setBuoyProximity(new Array(scaledBuoys.length).fill(false));

        const scaledLine = scaleStartFinishLine(START_FINISH_LINE_PERCENT, width, height);
        setStartFinishLine(scaledLine);

        // Generate coastline for the world
        const newCoast = generateCoastLine(width, height);
        setCoast(newCoast);

        // Initial boat position in world coordinates
        const initialPos = { x: width * 0.35, y: height * 0.7 };
        setBoat(prev => ({
          ...prev,
          position: initialPos,
        }));

        // Initialize camera to center on boat
        setCamera({
          x: initialPos.x - width / 2,
          y: initialPos.y - height / 2,
        });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const togglePause = useCallback(() => {
    setGameState((prev) => ({ ...prev, isPaused: !prev.isPaused }));
  }, []);

  useKeyboardInput(setRudderInput, togglePause);

  // Constrain position only for land collision (coast)
  const constrainForLand = useCallback(
    (pos: Vector2D, velocity: Vector2D): { position: Vector2D; velocity: Vector2D } => {
      // Check collision with land (right and top of the world)
      const waterConstraint = constrainToWater(pos.x, pos.y, worldSize.width, worldSize.height, 15, coast);

      if (waterConstraint.hitLand) {
        return {
          position: { x: waterConstraint.x, y: waterConstraint.y },
          velocity: { x: velocity.x * 0.3, y: velocity.y * 0.3 },
        };
      }

      return { position: pos, velocity };
    },
    [worldSize, coast]
  );

  const checkRaceProgress = useCallback(
    (prevPos: Vector2D, newPos: Vector2D, heading: number) => {
      if (gameState.mode !== 'race' || gameState.raceStatus !== 'racing') return;

      const currentBuoyIndex = gameState.currentBuoyIndex;

      if (currentBuoyIndex >= buoys.length) {
        if (startFinishLine && crossedLine(prevPos, newPos, startFinishLine.start, startFinishLine.end)) {
          setGameState((prev) => ({
            ...prev,
            raceStatus: 'finished',
          }));
        }
        return;
      }

      const currentBuoy = buoys[currentBuoyIndex];
      const wasNear = buoyProximity[currentBuoyIndex];
      const isNear = distance(newPos, currentBuoy.position) < ROUNDING_ZONE_RADIUS;

      if (wasNear && !isNear) {
        if (checkBuoyRounded(prevPos, newPos, currentBuoy, heading)) {
          setGameState((prev) => ({
            ...prev,
            buoysRounded: [...prev.buoysRounded, currentBuoyIndex],
            currentBuoyIndex: currentBuoyIndex + 1,
          }));
        }
      }

      setBuoyProximity(prev => {
        const newProx = [...prev];
        newProx[currentBuoyIndex] = isNear;
        return newProx;
      });
    },
    [gameState.mode, gameState.raceStatus, gameState.currentBuoyIndex, buoys, buoyProximity, startFinishLine]
  );

  const update = useCallback(
    (deltaTime: number) => {
      updateWind(deltaTime);

      setBoat((prevBoat) => {
        const updatedBoat = updateBoatPhysics(
          { ...prevBoat, rudderInput },
          wind,
          deltaTime
        );

        // Only constrain for land collision
        const constrained = constrainForLand(updatedBoat.position, updatedBoat.velocity);

        checkRaceProgress(prevBoatPosRef.current, constrained.position, updatedBoat.heading);
        prevBoatPosRef.current = constrained.position;

        return {
          ...updatedBoat,
          position: constrained.position,
          velocity: constrained.velocity
        };
      });

      // Update camera to follow boat smoothly
      setCamera((prevCamera) => {
        const targetX = boat.position.x - canvasSize.width / 2;
        const targetY = boat.position.y - canvasSize.height / 2;

        return {
          x: lerp(prevCamera.x, targetX, CAMERA_SMOOTHING),
          y: lerp(prevCamera.y, targetY, CAMERA_SMOOTHING),
        };
      });

      if (gameState.raceStatus === 'racing') {
        setGameState((prev) => ({
          ...prev,
          raceElapsedTime: Date.now() - (prev.raceStartTime || Date.now()),
        }));
      }
    },
    [
      rudderInput,
      wind,
      updateWind,
      constrainForLand,
      checkRaceProgress,
      gameState.raceStatus,
      boat.position,
      canvasSize,
    ]
  );

  const render = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      renderFrame(
        ctx,
        boat,
        wind,
        buoys,
        gameState,
        canvasSize.width,
        canvasSize.height,
        isDarkMode,
        startFinishLine,
        BOAT_SCALE,
        coast,
        camera
      );
    },
    [boat, wind, buoys, gameState, canvasSize, isDarkMode, startFinishLine, coast, camera]
  );

  useGameLoop(canvasRef, update, render, gameState.isPaused || showInstructions);

  const handleSetMode = useCallback((mode: 'free' | 'race') => {
    setGameState((prev) => ({
      ...prev,
      mode,
      raceStatus: 'notStarted',
      raceStartTime: null,
      raceElapsedTime: 0,
      currentBuoyIndex: 0,
      buoysRounded: [],
    }));
    setBuoyProximity(new Array(buoys.length).fill(false));
  }, [buoys.length]);

  const handleStartRace = useCallback(() => {
    const startPos = { x: worldSize.width * 0.35, y: worldSize.height * 0.7 };

    setBoat(prev => ({
      ...INITIAL_BOAT_STATE,
      position: startPos,
    }));

    setCamera({
      x: startPos.x - canvasSize.width / 2,
      y: startPos.y - canvasSize.height / 2,
    });

    setGameState((prev) => ({
      ...prev,
      raceStatus: 'countdown',
      countdownValue: 3,
      currentBuoyIndex: 0,
      buoysRounded: [],
    }));
    setBuoyProximity(new Array(buoys.length).fill(false));

    let count = 3;
    const countdownInterval = setInterval(() => {
      count--;
      if (count > 0) {
        setGameState((prev) => ({ ...prev, countdownValue: count }));
      } else {
        clearInterval(countdownInterval);
        setGameState((prev) => ({
          ...prev,
          raceStatus: 'racing',
          raceStartTime: Date.now(),
          raceElapsedTime: 0,
        }));
      }
    }, 1000);
  }, [worldSize, canvasSize, buoys.length]);

  const handleResetRace = useCallback(() => {
    const startPos = { x: worldSize.width * 0.35, y: worldSize.height * 0.7 };

    setBoat({
      ...INITIAL_BOAT_STATE,
      position: startPos,
    });

    setCamera({
      x: startPos.x - canvasSize.width / 2,
      y: startPos.y - canvasSize.height / 2,
    });

    setGameState((prev) => ({
      ...prev,
      raceStatus: 'notStarted',
      raceStartTime: null,
      raceElapsedTime: 0,
      currentBuoyIndex: 0,
      buoysRounded: [],
    }));
    setBuoyProximity(new Array(buoys.length).fill(false));
  }, [worldSize, canvasSize, buoys.length]);

  return (
    <div className="relative w-full h-full min-h-[500px]">
      <canvas
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}
        className="w-full h-full"
        tabIndex={0}
      />

      {showInstructions && (
        <Instructions onClose={() => setShowInstructions(false)} />
      )}

      {!showInstructions && (
        <>
          <HUD boat={boat} />
          <ModeSelector
            mode={gameState.mode}
            setMode={handleSetMode}
            onStartRace={handleStartRace}
            onResetRace={handleResetRace}
            raceStatus={gameState.raceStatus}
          />
          {gameState.mode === 'race' && (
            <RaceTimer
              raceStatus={gameState.raceStatus}
              elapsedTime={gameState.raceElapsedTime}
              buoysRounded={gameState.buoysRounded.length}
              totalBuoys={buoys.length}
            />
          )}
        </>
      )}
    </div>
  );
};

export default Sailing;
